<template>
  <div class="visitor-parent-search" ref="dynamicSelect">
    <div class="input-container" @click="toggleOptions">
      <input
        :id="searchInputId"
        ref="searchInputRef"
        class="input"
        :class="{ open: showOptions }"
        type="text"
        role="combobox"
        :aria-expanded="showOptions ? 'true' : 'false'"
        :aria-controls="listboxId"
        aria-haspopup="listbox"
        aria-autocomplete="list"
        :placeholder="placeholder"
        :value="inputValue"
        :aria-label="inputId ? undefined : placeholder || undefined"
        @input="onInput"
        @keydown="onInputKeydown"
      />
      <span class="chevron" :class="{ open: showOptions }" aria-hidden="true">
        <i class="ri-arrow-down-s-line"></i>
      </span>
    </div>
    <ul
      v-if="showOptions"
      :id="listboxId"
      ref="listboxRef"
      role="listbox"
      :aria-label="placeholder"
      class="options ps__child--consume"
    >
      <template v-if="options.length">
        <li
          v-for="(option, index) in options"
          :key="`${option.label}-${index}`"
          class="visitor-parent-search__li"
          role="presentation"
        >
          <button
            type="button"
            role="option"
            tabindex="-1"
            class="visitor-parent-search__option"
            :aria-selected="
              isSameOptionValue(option.value, modelValue) ? 'true' : 'false'
            "
            @click="selectOption(option)"
            @keydown="onOptionKeydown($event, index, option)"
          >
            <slot v-if="hasOptionSlot" name="option" :value="option"></slot>
            <template v-else>
              {{ option.label }}
            </template>
          </button>
        </li>
      </template>
      <template v-else>
        <li class="no-results">Sorry, no matching options</li>
      </template>
    </ul>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from "vue"
import { useI18n } from "vue-i18n"
import { announce } from "@/composables/useLiveAnnouncer"

export default {
  name: "VisitorParentSearch",
  props: {
    modelValue: {
      type: Object,
      default: null
    },
    options: {
      type: Array,
      default: () => [],
      validator: (options) => {
        return options.every((option) => {
          return option.label && option.value
        })
      }
    },
    placeholder: {
      type: String,
      default: ""
    },
    /** When set, associates the combobox with an external `<label for>` (APG). */
    inputId: {
      type: String,
      default: ""
    }
  },
  emits: ["update:modelValue", "search"],
  setup(props, { emit, slots }) {
    const { t } = useI18n()
    const uid = Math.random().toString(36).slice(2, 11)
    const searchInputId = computed(
      () => props.inputId?.trim() || `visitor-parent-search-in-${uid}`
    )
    const listboxId = `visitor-parent-search-lb-${uid}`

    const inputValue = ref("")
    const showOptions = ref(false)
    const dynamicSelect = ref(null)
    const searchInputRef = ref(null)
    const listboxRef = ref(null)

    const hasOptionSlot = computed(() => !!slots.option)

    const isSameOptionValue = (a, b) => {
      if (a == null || b == null) return false
      if (a === b) return true
      const idA = a?.id ?? a?.parent_id
      const idB = b?.id ?? b?.parent_id
      if (idA != null && idB != null) return String(idA) === String(idB)
      return false
    }

    const onInput = (event) => {
      inputValue.value = event.target.value
      showOptions.value = true
      emit("search", inputValue.value)
    }

    const selectOption = (option) => {
      inputValue.value = option.label
      emit("update:modelValue", option.value)
      emit("search", inputValue.value)
      showOptions.value = false
    }

    const toggleOptions = () => {
      showOptions.value = !showOptions.value
    }

    const getOptionButtons = () => {
      const root = listboxRef.value
      if (!root?.querySelectorAll) return []
      return Array.from(root.querySelectorAll(".visitor-parent-search__option"))
    }

    const focusFirstOption = () => {
      nextTick(() => {
        const buttons = getOptionButtons()
        buttons[0]?.focus?.()
      })
    }

    const focusLastOption = () => {
      nextTick(() => {
        const buttons = getOptionButtons()
        buttons[buttons.length - 1]?.focus?.()
      })
    }

    const onInputKeydown = (event) => {
      if (event.key === "Tab") {
        showOptions.value = false
        return
      }
      if (event.key === "Escape") {
        showOptions.value = false
        return
      }
      if (event.key === "ArrowDown" && props.options.length > 0) {
        event.preventDefault()
        showOptions.value = true
        nextTick(() => focusFirstOption())
        return
      }
      if (
        event.key === "ArrowUp" &&
        showOptions.value &&
        props.options.length > 0
      ) {
        event.preventDefault()
        focusLastOption()
      }
    }

    const onOptionKeydown = (event, index, option) => {
      const buttons = getOptionButtons()
      const lastIndex = buttons.length - 1
      if (event.key === "Escape") {
        event.preventDefault()
        showOptions.value = false
        searchInputRef.value?.focus?.()
        return
      }
      if (event.key === "ArrowDown") {
        event.preventDefault()
        if (index < lastIndex) {
          buttons[index + 1]?.focus?.()
        }
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        if (index > 0) {
          buttons[index - 1]?.focus?.()
        } else {
          searchInputRef.value?.focus?.()
        }
        return
      }
      if (event.key === "Home") {
        event.preventDefault()
        buttons[0]?.focus?.()
        return
      }
      if (event.key === "End") {
        event.preventDefault()
        buttons[lastIndex]?.focus?.()
        return
      }
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        selectOption(option)
      }
    }

    watch(
      () => props.options.length,
      (len) => {
        if (
          len > 0 &&
          typeof document !== "undefined" &&
          document.activeElement === searchInputRef.value
        ) {
          showOptions.value = true
        }
      }
    )

    /* In-place aria-live is unreliable in NVDA; mirror empty search via global announcer (2189113). */
    watch(
      () => [props.options.length, inputValue.value.trim(), showOptions.value],
      ([optionCount, query, isOpen]) => {
        if (!isOpen || !query || optionCount > 0) return
        const fieldName = props.placeholder?.trim?.() || ""
        const msg = t("common.sorry_no_matching_options")
        announce(fieldName ? `${fieldName}. ${msg}` : msg, { assertive: true })
      }
    )

    const handleClickOutside = (event) => {
      if (dynamicSelect.value && !dynamicSelect.value.contains(event.target)) {
        showOptions.value = false
      }
    }

    onMounted(() => {
      document.addEventListener("click", handleClickOutside)
    })

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside)
    })

    return {
      hasOptionSlot,
      inputValue,
      showOptions,
      onInput,
      selectOption,
      toggleOptions,
      dynamicSelect,
      searchInputId,
      listboxId,
      searchInputRef,
      listboxRef,
      onInputKeydown,
      onOptionKeydown,
      isSameOptionValue
    }
  }
}
</script>

<style scoped>
.visitor-parent-search__li {
  list-style: none;
  margin: 0;
  padding: 0;
}

.visitor-parent-search__option {
  display: block;
  width: 100%;
  margin: 0;
  padding: inherit;
  border: 0;
  background: transparent;
  font: inherit;
  text-align: inherit;
  cursor: pointer;
}

.visitor-parent-search__option:focus-visible {
  outline: 2px solid var(--v3-focus-outline, #2b6cb0);
  outline-offset: 1px;
}
</style>
